import { BaseProps, VirtualList } from '#components';
import { addDaysAndSetStartOfDay_DEPRECATED, assertIsoTimestamp, formatToTimeZone, IsoTimestamp } from '#datetime';
import { TimeIcon } from '#icons';
import { inputBaseClasses, outlinedInputClasses, TextField, Autocomplete, FilterOptionsState } from '@mui/material';
import { parseISO } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useCallback } from 'react';

type Props = BaseProps & {
  /**
   * Time to initialize the date time picker.
   */
  time: IsoTimestamp;

  /**
   * Timezone to initialize the date time picker.
   */
  timezone: string;

  /**
   * Show icon for the time picker.
   */
  forcePopupIcon?: boolean;

  /**
   * Should be passed when the TimePicker is used as end time part of TimeRange
   */
  timeRangeUtcStartOfDayForEndTimeSelector?: IsoTimestamp;

  /**
   * Icon to display to the left of the input field
   */
  startAdornment?: React.ReactNode;

  error?: boolean;

  disabled?: boolean;

  onChange: (time: IsoTimestamp) => void;
};

type TimeOption = { id: string; value: string };
const TIME_OPTIONS: TimeOption[] = [];

for (let i = 0; i < 24; i++) {
  for (let j = 0; j < 60; j += 1) {
    const dMinutes = j.toString().length === 2 ? j.toString() : `0${j.toString()}`;
    const meridiem = i < 12 ? ' am' : ' pm';
    TIME_OPTIONS.push({
      id: `${i < 10 ? '0' : ''}${i}:${dMinutes}`, // formaat '01:00, 12:30, 16:00
      value: `${i % 12 === 0 ? '12' : i % 12}:${dMinutes}${meridiem}`,
    });
  }
}

// If characters of searchStr is present in input in order then returns true
const fuzzySearch = (input: string, searchStr: string): boolean => {
  let index = -1;
  for (let i = 0; i < searchStr.length; i++) {
    index = input.indexOf(searchStr.charAt(i), index + 1);
    if (index === -1) {
      return false;
    }
  }

  return true;
};

const TimePicker = ({
  dataTestId,
  time,
  timezone,
  forcePopupIcon = false,
  timeRangeUtcStartOfDayForEndTimeSelector,
  startAdornment,
  error,
  disabled,
  onChange,
}: Props) => {
  const defaultTime = formatToTimeZone(time, 'HH:mm', timezone);
  const defaultSelectedValue = TIME_OPTIONS.find((option) => option.id === defaultTime);

  const filterOptions = (options: TimeOption[], state: FilterOptionsState<TimeOption>) => {
    const { inputValue } = state;

    const sort = (a: string, b: string) => {
      const aHour = parseInt(a.split(':')[0], 10);
      const bHour = parseInt(b.split(':')[0], 10);
      return aHour - bHour;
    };

    // If no input
    if (!inputValue) {
      const minutes = toZonedTime(time, timezone).getMinutes();
      const filterOption = (option: TimeOption, baseMins: number) => {
        if (!option.value) {
          return false;
        }
        const value = option.value.split(':')[1].slice(0, 2);
        const valueMin = parseInt(value, 10);
        return valueMin % baseMins === 0;
      };

      if (minutes % 30 === 0) {
        return options.filter((option) => filterOption(option, 30));
      }

      if (minutes % 15 === 0) {
        return options.filter((option) => filterOption(option, 15));
      }

      if (minutes % 10 === 0) {
        return options.filter((option) => filterOption(option, 10));
      }

      if (minutes % 5 === 0) {
        return options.filter((option) => filterOption(option, 5));
      }

      // Return all options if none of the above conditions are met.
      // E.g. user has input time as 3:41am
      return options;
    }

    // Show times in 30 mins increment and filter based on input value
    // - If 1 character
    // - If 2 character and second character is 'a' or 'p'
    // - If 2 characters and second character is number (here we are assuming first character is also a number)
    if (
      inputValue.length === 1 ||
      (inputValue.length === 2 && (inputValue.charAt(1) === 'a' || inputValue.charAt(1) === 'p')) ||
      (inputValue.length === 2 && !Number.isNaN(parseInt(inputValue.charAt(1), 10)))
    ) {
      const results = options
        .filter((option) => option.value && (option.value.indexOf(':00') !== -1 || option.value.indexOf(':30') !== -1))
        .filter((option) => option.value && fuzzySearch(option.value, inputValue))
        .filter((option) => option.value && option.value.charAt(0) === inputValue.charAt(0))
        .sort((a, b) => (a.value && b.value ? sort(a.value, b.value) : 0));

      if (results.length) {
        return results;
      }
    }

    // inputString like 3am
    if (
      inputValue.length === 3 &&
      Number.isNaN(parseInt(inputValue.charAt(1), 10)) &&
      Number.isNaN(parseInt(inputValue.charAt(2), 10))
    ) {
      const results = options
        .filter((option) => option.value && option.value.indexOf(':00') !== -1)
        .filter((option) => option.value && fuzzySearch(option.value, inputValue))
        .filter((option) => option.value && option.value.charAt(0) === inputValue.charAt(0))
        .sort((a, b) => (a.value && b.value ? sort(a.value, b.value) : 0));

      if (results.length) {
        return results;
      }
    }

    if (inputValue.length === 3 && Number.isNaN(parseInt(inputValue.charAt(2), 10))) {
      const results = options
        .filter((option) => option.value && option.value.indexOf(':00') !== -1)
        .filter((option) => option.value?.startsWith(inputValue.slice(0, 2)))
        .filter((option) => option.value && fuzzySearch(option.value, inputValue))
        .sort((a, b) => (a.value && b.value ? sort(a.value, b.value) : 0));

      if (results.length) {
        return results;
      }
    }

    if (
      inputValue.length === 4 &&
      Number.isNaN(parseInt(inputValue.charAt(2), 10)) &&
      Number.isNaN(parseInt(inputValue.charAt(3), 10))
    ) {
      const results = options
        .filter((option) => option.value && option.value.indexOf(':00') !== -1)
        .filter((option) => option.value && fuzzySearch(option.value, inputValue))
        .filter((option) => option.value && option.value.charAt(0) === inputValue.charAt(0))
        .sort((a, b) => (a.value && b.value ? sort(a.value, b.value) : 0));

      if (results.length) {
        return results;
      }
    }

    // If none of the above condition are true then filter on input value.
    // This will show options with 1 min increment.
    return options
      .filter((option) => option.value && fuzzySearch(option.value, inputValue))
      .filter((option) => option.value && option.value.charAt(0) === inputValue.charAt(0))
      .sort((a, b) => (a.value && b.value ? sort(a.value, b.value) : 0));
  };

  const updateTime = useCallback(
    (value: string) => {
      if (!value) {
        return;
      }

      let newTime = toZonedTime(time, timezone);

      // If timeRangeUtcStartOfDayForEndTimeSelector !== null then always use it as the base time.
      if (timeRangeUtcStartOfDayForEndTimeSelector) {
        if (value === '00:00') {
          newTime = parseISO(addDaysAndSetStartOfDay_DEPRECATED(timeRangeUtcStartOfDayForEndTimeSelector, timezone, 1));
        } else {
          newTime = toZonedTime(timeRangeUtcStartOfDayForEndTimeSelector, timezone);
        }
      }

      const hoursMinutes = value.split(':');
      newTime.setHours(parseInt(hoursMinutes[0], 10));
      newTime.setMinutes(parseInt(hoursMinutes[1], 10));
      newTime.setSeconds(0);

      const newUtcTime = fromZonedTime(newTime, timezone);

      console.log('newUtcTime', newUtcTime);
      onChange(assertIsoTimestamp(newUtcTime.toISOString()));
    },
    [time, timezone, timeRangeUtcStartOfDayForEndTimeSelector, onChange]
  );

  const validateTime = (value: string) => {
    let isValid = /^((1[0-2]|0?[1-9]):([0-5][0-9])(\s)?([AaPp][Mm]))$/.test(value);
    isValid = isValid || /^0[0-9]:([0-5][0-9])$/.test(value);
    return isValid;
  };

  const handleChange = (_: React.SyntheticEvent<Element, Event>, value: TimeOption) => {
    if (validateTime(value.value)) {
      updateTime(value.id);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.target.select();
  };

  const handleBlur = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (validateTime(event.target.value)) {
        const timeOption = TIME_OPTIONS.find((option) => option.value === event.target.value);
        if (!timeOption) {
          return;
        }
        // SAFETY: TIME_OPTIONS uses string identifiers accepted by updateTime.
        updateTime(timeOption.id as string);
      }
    },
    [updateTime]
  );

  return (
    <Autocomplete
      autoHighlight
      clearOnBlur
      disableClearable
      disableListWrap
      fullWidth
      openOnFocus
      data-testid={dataTestId}
      disabled={disabled}
      forcePopupIcon={forcePopupIcon}
      popupIcon={<TimeIcon />}
      value={defaultSelectedValue}
      options={TIME_OPTIONS}
      getOptionLabel={(option) => option.value ?? ''}
      // SAFETY: VirtualList forwards the HTML attributes Autocomplete gives its listbox component.
      ListboxComponent={VirtualList as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
      renderInput={(params) => (
        <TextField
          {...params}
          onFocus={handleFocus}
          onBlur={handleBlur}
          InputProps={{ ...params.InputProps, startAdornment }}
          error={error}
        />
      )}
      filterOptions={filterOptions}
      onChange={handleChange}
      // eslint-disable-next-line no-restricted-syntax
      sx={{
        minWidth: '120px',
        [`& .${inputBaseClasses.root}`]: {
          height: '32px',
        },
        [`& .${outlinedInputClasses.root}.${inputBaseClasses.sizeSmall}`]: {
          padding: '3px 8px',
        },
      }}
    />
  );
};

export default TimePicker;
